import React from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Link } from "react-router-dom";

const steps = [
  "Sign up for the 1 month free trial.",
  "Get a free onboarding call to get you started.",
  "Import your programs and invite your clients.",
  "Start coaching your clients.",
  "Track your clients progress and stay organized.",
];

const trialFeatures = [
  "Unlimited Slots",
  "White Glove Service",
  "Onboarding Call",
  "Import Programs",
];

const detailSections = [
  {
    title: "Start simple",
    body: "Create your coach account and get immediate access to the tools you need to run training through Trainer App. No contracts, hidden fees, or bloated setup.",
  },
  {
    title: "Move over with help",
    body: "Our white glove onboarding helps you bring over clients and programs so the transition feels clear from day one.",
  },
  {
    title: "Coach your way",
    body: "Schedule clients, build personalized plans, upload content, and keep training history in one straightforward workspace.",
  },
  {
    title: "Grow without surprises",
    body: "Use the free trial to test your workflow, then scale client slots as your business grows. Cancel any time.",
  },
];

const HowItWorks = () => {
  return (
    <Box sx={{ bgcolor: "#fff", color: "#111" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, md: 5 },
            mb: 2,
            overflow: "hidden",
          }}
        >
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2.2rem", sm: "3rem", md: "4rem" },
                  fontWeight: 900,
                  lineHeight: 0.95,
                  letterSpacing: 0.5,
                  mb: 2,
                }}
              >
                How Does It{" "}
                <Box component="span" sx={{ color: "#ef0000" }}>
                  Work?
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.05rem", md: "1.25rem" },
                  lineHeight: 1.45,
                  maxWidth: 620,
                  mb: 3,
                }}
              >
                The features you need, that completely make sense. Trainer App
                keeps onboarding, programming, scheduling, and client management
                simple from the first trial day.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to="/coaching"
                size="large"
                sx={{ fontWeight: 700, px: 4 }}
              >
                Start Free Trial
              </Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2.5, md: 3 },
                  bgcolor: "#fafafa",
                  height: "100%",
                }}
              >
                <Typography
                  sx={{
                    color: "#ef0000",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    mb: 1,
                  }}
                >
                  1 Month Free Trial
                </Typography>
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, mb: 2 }}>
                  No contracts, no hidden fees, cancel any time.
                </Typography>
                <Stack spacing={1.25}>
                  {trialFeatures.map((feature) => (
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      key={feature}
                    >
                      <CheckCircleOutlineIcon
                        sx={{ color: "#ef0000", fontSize: 22 }}
                      />
                      <Typography sx={{ fontWeight: 700 }}>
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "1.9rem" },
                  fontWeight: 800,
                  mb: 2,
                }}
              >
                Five steps to coaching faster
              </Typography>
              <Stack divider={<Divider flexItem />} spacing={0}>
                {steps.map((step, index) => (
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="flex-start"
                    sx={{ py: 1.6 }}
                    key={step}
                  >
                    <Typography
                      sx={{
                        color: "#ef0000",
                        fontSize: "1.4rem",
                        fontWeight: 900,
                        lineHeight: 1,
                        minWidth: 34,
                      }}
                    >
                      {index + 1}.
                    </Typography>
                    <Typography sx={{ fontSize: "1.05rem", lineHeight: 1.45 }}>
                      {step}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              {detailSections.map((section) => (
                <Grid item xs={12} sm={6} key={section.title}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 2.5, md: 3 },
                      height: "100%",
                      bgcolor: "#fff",
                    }}
                  >
                    <Typography
                      component="h3"
                      sx={{
                        fontSize: "1.15rem",
                        fontWeight: 800,
                        mb: 1,
                      }}
                    >
                      {section.title}
                    </Typography>
                    <Typography
                      sx={{ color: "text.secondary", lineHeight: 1.6 }}
                    >
                      {section.body}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: { xs: 3, md: 4 },
            textAlign: "center",
            bgcolor: "#fafafa",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.4rem", md: "1.9rem" },
              fontWeight: 800,
              mb: 1,
            }}
          >
            Sign Up Today!
          </Typography>
          <Typography sx={{ maxWidth: 720, mx: "auto", mb: 3 }}>
            Try Trainer App with the full onboarding experience and see how
            quickly your coaching workflow can get cleaner.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/coaching"
            sx={{ fontWeight: 700, px: 4 }}
          >
            Start Free Trial
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default HowItWorks;
